import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import PostUpdateModal, { type LocalUpdate } from './components/PostUpdateModal';
import ProjectIntentSection from '@/components/feature/ProjectIntentSection';
import ProjectStoryTimeline from '@/components/feature/ProjectStoryTimeline';
import FilesTab from './components/FilesTab';
import PhotosTab from './components/PhotosTab';
import ScheduleTab from './components/ScheduleTab';
import TasksTab from './components/TasksTab';
import ChecklistTab from './components/ChecklistTab';
import NotificationDetailSheet from '../notifications/NotificationDetailSheet';
import type { NotificationItem } from '../notifications/page';
import { useAuth } from '@/hooks/useAuth';
import PhotoLightbox, { type LightboxPhoto } from '@/components/feature/PhotoLightbox';
import { supabase } from '@/lib/supabase';
import { getProjectScope } from '@/lib/projectScope';
import StagesTab from './components/StagesTab';
import DailyLogTab from './components/DailyLogTab';
import { useProjectProgressSummary } from '@/hooks/useProjectProgressSummary';
import { useProjectSummary } from '@/hooks/useProjectSummary';
import NewInstructionModal from '@/components/feature/NewInstructionModal';
import { type ScheduleItem } from '@/lib/scheduleUtils';
import { getProjectCoverImage, getDefaultCoverUrl } from '@/lib/projectCover';
import { formatDate, timeAgoKey } from '@/lib/dateUtils';
import ManagementInstructionsTab from './components/InstructionsTab';
import ChangeOrdersTab from './components/ChangeOrdersTab';
import PaymentsTab from './components/PaymentsTab';
import {
  SkeletonHero,
  SkeletonStatRow,
  SkeletonCard,
  SkeletonStageTimeline,
  SkeletonPhotoGrid,
  SkeletonText,
  SkeletonProgressBar,
  SkeletonBase,
  SkeletonListRow,
} from '@/components/base/Skeleton';
import {
  FILE_OPEN_ERROR_MESSAGE,
  openProjectFileInNewTab,
  resolveFileDisplayUrl,
  useSignedFileUrls,
} from '@/lib/storageSignedUrl';

const typeConfig: Record<string, { icon: string; color: string }> = {
  progress:   { icon: 'ri-checkbox-circle-line',  color: '#059669' },
  issue:      { icon: 'ri-error-warning-line',     color: '#EF4444' },
  inspection: { icon: 'ri-shield-check-line',      color: '#059669' },
  approval:   { icon: 'ri-file-list-3-line',       color: '#E8621A' },
  question:   { icon: 'ri-question-answer-line',   color: '#E8621A' },
  task:       { icon: 'ri-task-line',              color: '#F59E0B' },
  system:     { icon: 'ri-information-line',       color: '#9CA3AF' },
  payment:    { icon: 'ri-bank-card-line',         color: '#059669' },
  update:     { icon: 'ri-megaphone-line',         color: '#E8621A' },
};

function getTypeConfig(type: string) {
  return typeConfig[type?.toLowerCase()] || { icon: 'ri-notification-3-line', color: '#9CA3AF' };
}

// timeAgo is now locale-aware — used via t() inside the component

interface RealProject {
  id: string;
  project_name: string;
  project_code: string;
  project_type: string;
  client_name: string | null;
  site_address: string | null;
  status: string;
  start_date: string | null;
  target_completion_date: string | null;
  summary: string | null;
  cover_image: string | null;
  created_at: string;
}

interface RealStage {
  id: string;
  stage_name: string;
  sort_order: number;
  status: string;
  completion_percent: number;
  planned_start_date: string | null;
  planned_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  delay_reason: string | null;
}

interface ProgressUpdate {
  id: string;
  project_id: string;
  title: string;
  body: string | null;
  update_type: string;
  posted_by_role: string;
  created_at: string;
  visibility: string;
  stage_name: string | null;
}

interface ClientQuestion {
  id: string;
  question_title: string;
  body: string;
  client_user_id: string;
  created_at: string;
  status: string;
  asker_name?: string;
}

interface QuestionReply {
  id: string;
  question_id: string;
  author_user_id: string;
  body: string;
  created_at: string;
}

interface QuestionAttachment {
  id: string;
  question_id: string;
  file_id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  file_size: string | number | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile: authProfile, loading: authLoading } = useAuth();
  const { t } = useTranslation();

  function timeAgo(iso: string): string {
    const { key, params } = timeAgoKey(iso);
    return t(key, params);
  }

  // Build translated tabs
  const tabs = [
    t('tabs.overview'),
    t('tabs.stages'),
    t('tabs.schedule'),
    t('tabs.checklist'),
    t('tabs.dailyLog'),
    t('tabs.instructions'),
    t('tabs.changeOrders'),
    t('tabs.photos'),
    t('tabs.files'),
    t('tabs.tasks'),
    t('tabs.payments'),
    t('tabs.notifications'),
    t('tabs.questions'),
  ];

  const TAB_KEYS = ['Overview', 'Stages', 'Schedule', 'Checklist', 'Daily Log', 'Instructions', 'Change Orders', 'Photos', 'Files', 'Tasks', 'Payments', 'Notifications', 'Questions'];

  const initialTab = searchParams.get('tab');
  const validTabIndex = initialTab ? TAB_KEYS.indexOf(initialTab) : 0;
  const [activeTabIndex, setActiveTabIndex] = useState(validTabIndex >= 0 ? validTabIndex : 0);
  const activeTab = TAB_KEYS[activeTabIndex];

  const [lightboxPhotos, setLightboxPhotos] = useState<LightboxPhoto[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [localUpdates, setLocalUpdates] = useState<LocalUpdate[]>([]);
  const [selectedNotif, setSelectedNotif] = useState<NotificationItem | null>(null);
  const [answerText, setAnswerText] = useState<Record<string, string>>();
  const [showEditMenu, setShowEditMenu] = useState(false);
  const [questionReplies, setQuestionReplies] = useState<QuestionReply[]>([]);
  const [questionAttachments, setQuestionAttachments] = useState<QuestionAttachment[]>([]);
  const [questionAttachmentOpenError, setQuestionAttachmentOpenError] = useState('');

  // Instruction create modal state
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [instructionLinkedScheduleItemId, setInstructionLinkedScheduleItemId] = useState<string | undefined>();
  const [instructionLinkedChecklistItemId, setInstructionLinkedChecklistItemId] = useState<string | undefined>();
  const [instructionLinkedPhotoId, setInstructionLinkedPhotoId] = useState<string | undefined>();
  const [instructionDefaultTitle, setInstructionDefaultTitle] = useState('');

  const openInstructionForSchedule = useCallback((item: ScheduleItem) => {
    setInstructionLinkedScheduleItemId(item.id);
    setInstructionLinkedChecklistItemId(undefined);
    setInstructionLinkedPhotoId(undefined);
    setInstructionDefaultTitle(`Action required: ${item.title}`);
    setShowInstructionModal(true);
  }, []);

  const [archiveActionLoading, setArchiveActionLoading] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [archivedAt, setArchivedAt] = useState<string | null>(null);

  // Real data state
  const [project, setProject] = useState<RealProject | null>(null);
  const [stages, setStages] = useState<RealStage[]>();
  const [progressUpdates, setProgressUpdates] = useState<ProgressUpdate[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [questions, setQuestions] = useState<ClientQuestion[]>([]);
  const [loadingProject, setLoadingProject] = useState(true);
  const [loadingNotifs, setLoadingNotifs] = useState(true);

  const openLightbox = (photos: LightboxPhoto[], startIndex: number) => {
    setLightboxPhotos(photos);
    setLightboxIndex(startIndex);
  };

  // Use the DIRECT Supabase query hook — no stale cache, always fresh
  const liveSummary = useProjectSummary(project?.id, { enabled: !!project?.id });
  // Also keep the stage-breakdown hook for stagesSummary (used in stage prompt)
  const summary = useProjectProgressSummary(project?.id || '');

  const questionAttachmentUrls = useSignedFileUrls(questionAttachments);

  // Only wait for summary after project header is loaded and we have a project id.
  const isFirstLoad =
    authLoading ||
    loadingProject ||
    (Boolean(project?.id) && !liveSummary.loaded);

  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  // Load project + stages (header only)
  async function loadProjectHeader() {
    setLoadingProject(true);
    const { data: proj } = await supabase
      .from('jphph_projects')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (proj) {
      setProject(proj as RealProject);
      setIsArchived((proj as any).is_archived === true);
      setArchivedAt((proj as any).archived_at || null);
    }

    const { data: stageRows } = await supabase
      .from('jphph_project_stages')
      .select('*')
      .eq('project_id', id)
      .order('sort_order', { ascending: true });

    setStages((stageRows || []) as RealStage[]);
    setLoadingProject(false);
  }

  useEffect(() => {
    if (!id || authLoading) return;

    async function checkAccessAndLoad() {
      setAccessDenied(false);
      setAccessError(null);
      setLoadingProject(true);

      const scope = await getProjectScope({
        profileId: authProfile?.id,
        role: authProfile?.user_type,
        userId: user?.id,
        userEmail: user?.email,
      });

      if (scope.error && !scope.isFullAccess) {
        setAccessError(scope.error);
        setLoadingProject(false);
        return;
      }

      if (!scope.isFullAccess && scope.visibleProjectIds && !scope.visibleProjectIds.includes(id)) {
        setAccessDenied(true);
        setLoadingProject(false);
        return;
      }

      await loadProjectHeader();
    }

    checkAccessAndLoad();
  }, [id, authLoading, authProfile?.id, authProfile?.user_type, user?.id, user?.email]);

  async function handleArchiveProject(action: 'archive' | 'restore') {
    if (!project) return;
    setArchiveActionLoading(true);
    const now = new Date().toISOString();
    const { profile: _profile } = { profile: null as any };
    const updates: Record<string, unknown> = action === 'archive'
      ? { is_archived: true, archived_at: now, status: 'completed' }
      : { is_archived: false, archived_at: null, status: 'completed' };
    const { error } = await supabase.from('jphph_projects').update(updates).eq('id', project.id);
    if (!error) {
      setIsArchived(action === 'archive');
      setArchivedAt(action === 'archive' ? now : null);
    }
    setArchiveActionLoading(false);
  }

  // Load overview tab data only (progress updates, questions, etc.)
  async function loadOverviewData() {
    if (!id || overviewLoaded) return;
    setOverviewLoading(true);

    // Load progress updates
    const { data: updates } = await supabase
      .from('jphph_progress_updates')
      .select('*')
      .eq('project_id', id)
      .order('created_at', { ascending: false })
      .limit(50);
    setProgressUpdates((updates || []) as ProgressUpdate[]);

    // Load client questions
    const { data: qs } = await supabase
      .from('jphph_client_questions')
      .select('id, question_title, body, client_user_id, created_at, status')
      .eq('project_id', id)
      .order('created_at', { ascending: false });
    setQuestions((qs || []) as ClientQuestion[]);

    // Load replies for all questions in this project
    const { data: allReplies } = await supabase
      .from('jphph_client_question_replies')
      .select('*')
      .eq('project_id', id)
      .order('created_at', { ascending: true });
    setQuestionReplies((allReplies || []) as QuestionReply[]);

    // Load question attachments
    const { data: attachRows } = await supabase
      .from('jphph_client_question_attachments')
      .select('id, question_id, file_id')
      .eq('project_id', id);

    const aRows = (attachRows || []) as { id: string; question_id: string; file_id: string }[];
    if (aRows.length > 0) {
      const fileIds = aRows.map((a) => a.file_id);
      const { data: fData } = await supabase
        .from('jphph_project_files')
        .select('id, file_name, file_url, file_type, file_size, storage_bucket, storage_path')
        .in('id', fileIds);
      const filesById = new Map(
        ((fData || []) as Array<{
          id: string;
          file_name: string;
          file_url: string;
          file_type: string | null;
          file_size: string | number | null;
          storage_bucket?: string | null;
          storage_path?: string | null;
        }>).map((f) => [f.id, f])
      );
      const merged: QuestionAttachment[] = aRows.map((a) => {
        const f = filesById.get(a.file_id);
        return {
          id: a.id,
          question_id: a.question_id,
          file_id: a.file_id,
          file_name: f?.file_name || 'File',
          file_url: f?.file_url || '',
          file_type: f?.file_type || null,
          file_size: f?.file_size || null,
          storage_bucket: f?.storage_bucket ?? null,
          storage_path: f?.storage_path ?? null,
        };
      });
      setQuestionAttachments(merged);
    } else {
      setQuestionAttachments([]);
    }

    setOverviewLoading(false);
    setOverviewLoaded(true);
  }

  useEffect(() => {
    if ((activeTab === 'Overview' || activeTab === 'Questions') && id) {
      loadOverviewData();
    }
  }, [activeTab, id]);

  // Load notifications only when Notifications tab is active
  useEffect(() => {
    if (activeTab === 'Notifications' && id && user) {
      loadNotifications();
    }
  }, [activeTab, id, user]);

  const loadNotifications = useCallback(async () => {
    if (!user || !id) return;
    setLoadingNotifs(true);

    const { data: notifs } = await supabase
      .from('jphph_notifications')
      .select('*')
      .eq('project_id', id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (!notifs) { setLoadingNotifs(false); return; }

    // Get read status from notification_reads table
    const { data: reads } = await supabase
      .from('jphph_notification_reads')
      .select('notification_id')
      .eq('user_id', user.id);

    const readSet = new Set((reads || []).map((r) => r.notification_id));

    const enriched: NotificationItem[] = notifs.map((n) => {
      const cfg = getTypeConfig(n.notification_type);
      return {
        ...n,
        icon: cfg.icon,
        color: cfg.color,
        is_read: readSet.has(n.id) || n.is_read,
      };
    });

    setNotifications(enriched);
    setLoadingNotifs(false);
  }, [user, id]);

  async function markNotifRead(notifId: string) {
    if (!user) return;
    // Persist to notification_reads table
    await supabase.from('jphph_notification_reads').upsert(
      { notification_id: notifId, user_id: user.id },
      { onConflict: 'notification_id,user_id' }
    );
    // Also update is_read on the notification itself for the global count
    await supabase.from('jphph_notifications').update({ is_read: true }).eq('id', notifId);
    setNotifications((prev) => prev.map((n) => n.id === notifId ? { ...n, is_read: true } : n));
  }

  async function markAllNotifsRead() {
    if (!user) return;
    const unread = notifications.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    await supabase.from('jphph_notification_reads').upsert(
      unread.map((n) => ({ notification_id: n.id, user_id: user.id })),
      { onConflict: 'notification_id,user_id' }
    );
    await supabase.from('jphph_notifications').update({ is_read: true }).in('id', unread.map((n) => n.id));
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }

  async function submitAnswer(questionId: string) {
    const text = answerText?.[questionId];
    if (!text?.trim() || !user) return;

    await supabase.from('jphph_client_question_replies').insert({
      question_id: questionId,
      author_user_id: user.id,
      body: text.trim(),
      project_id: project?.id,
    });

    // Also mark question as answered
    await supabase
      .from('jphph_client_questions')
      .update({ status: 'answered' })
      .eq('id', questionId);

    setQuestionReplies((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        question_id: questionId,
        author_user_id: user.id,
        body: text.trim(),
        created_at: new Date().toISOString(),
      },
    ]);
    setAnswerText((prev) => ({ ...prev, [questionId]: '' }));
  }

  const handlePostSuccess = (newUpdate: LocalUpdate) => setLocalUpdates((prev) => [newUpdate, ...prev]);
  const unreadNotifCount = notifications.filter((n) => !n.is_read).length;

  // Use live summary for all progress numbers
  const summaryLoaded = liveSummary.loaded;
  const summaryLoading = liveSummary.loading;
  const projectProgress = liveSummary.projectProgress;
  const activeStage = liveSummary.activeStage;
  const managementCompleted = liveSummary.managementCompleted;
  const managementTotal = liveSummary.managementTotal;
  const subcontractorCompleted = liveSummary.subcontractorCompleted;
  const subcontractorTotal = liveSummary.subcontractorTotal;
  const subWaiting = liveSummary.subWaiting;
  const mgmtPct = liveSummary.mgmtPct;
  const subPct = liveSummary.subPct;



  // When ScheduleTab notifies us of a change, broadcast the sync event.
  // useProjectSummary listens for this event and re-queries Supabase directly.
  const handleScheduleUpdated = useCallback(() => {
    try {
      window.dispatchEvent(
        new CustomEvent('__jphph_schedule_sync__', {
          detail: {
            projectId: project?.id || '',
            isComplete: true,
            timestamp: new Date().toISOString(),
            source: 'ProjectDetail_handleScheduleUpdated',
          },
        })
      );
    } catch { /* noop */ }
  }, [project?.id]);

  const coverImage = project ? getProjectCoverImage(project) : getDefaultCoverUrl('Renovation');

  // Build stats array with proper loading handling — NEVER show fake 0/0
  const statsRows = (() => {
    // While loading (first time), show skeleton placeholders
    if (!liveSummary.loaded) {
      return [
        { label: t('progress.projectProgress'), value: '—', pct: null, color: '#E8621A', sub: t('progress.loading') },
        { label: t('progress.management'), value: '—', pct: null, color: '#059669', sub: t('progress.loading') },
        { label: t('progress.subcontractor'), value: '—', pct: null, color: '#F59E0B', sub: t('progress.loading') },
        { label: t('progress.stage'), value: '—', color: '#059669', sub: null },
      ];
    }

    return [
      {
        label: t('progress.projectProgress'),
        value: `${projectProgress}%`,
        pct: projectProgress,
        color: projectProgress === 100 ? '#059669' : '#E8621A',
        sub: `Sched ${liveSummary.scheduleFinalProgress}% · CL ${liveSummary.checklistFinalProgress}%`,
      },
      {
        label: t('progress.management'),
        value: `${managementCompleted}/${managementTotal}`,
        pct: mgmtPct,
        color: '#059669',
        sub: mgmtPct === 100 ? t('progress.done') : t('progress.pending'),
      },
      ...(subcontractorTotal > 0 ? [{
        label: t('progress.subcontractor'),
        value: `${subcontractorCompleted}/${subcontractorTotal}`,
        pct: subPct,
        color: subPct === 100 ? '#0891B2' : '#F59E0B',
        sub: subWaiting ? t('progress.waitingForSub') : t('progress.done'),
      }] : []),
      {
        label: t('progress.stage'),
        value: activeStage || '—',
        color: '#059669',
        sub: null,
      },
    ];
  })();

  // Stage and status from live summary — never from stale stagesSummary
  const currentStageName = liveSummary.loaded ? liveSummary.activeStage : '—';
  const projectStatus = liveSummary.loaded ? liveSummary.projectStatus : '—';

  // Show completion prompt when a stage completes
  const [showStagePrompt, setShowStagePrompt] = useState(false);
  const [pendingStageName, setPendingStageName] = useState<string | null>(null);

  if (accessError) {
    return (
      <MgmtLayout showBack backTo="/management/projects">
        <div className="text-center py-20 px-4">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-[#D1D5DB]">
            <i className="ri-error-warning-line text-5xl"></i>
          </div>
          <p className="text-[#374151] font-semibold">Could not verify project access.</p>
          <p className="text-[#9CA3AF] text-sm mt-2">{accessError}</p>
          <button onClick={() => navigate('/management/projects')} className="mt-3 text-[#E8621A] text-sm cursor-pointer">
            Back to Projects
          </button>
        </div>
      </MgmtLayout>
    );
  }

  if (accessDenied) {
    return (
      <MgmtLayout showBack backTo="/management/projects">
        <div className="text-center py-20">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-[#D1D5DB]">
            <i className="ri-lock-line text-5xl"></i>
          </div>
          <p className="text-[#374151] font-semibold">You do not have access to this project.</p>
          <button onClick={() => navigate('/management/projects')} className="mt-3 text-[#E8621A] text-sm cursor-pointer">
            Back to Projects
          </button>
        </div>
      </MgmtLayout>
    );
  }

  if (!loadingProject && !project) {
    return (
      <MgmtLayout showBack backTo="/management/projects">
        <div className="text-center py-20">
          <p className="text-[#374151] font-semibold">{t('error.projectNotFound')}</p>
          <button onClick={() => navigate('/management/projects')} className="mt-3 text-[#E8621A] text-sm cursor-pointer">
            {t('error.backToProjects')}
          </button>
        </div>
      </MgmtLayout>
    );
  }

  // ─── SKELETON LOADING STATE for first load ───
  if (isFirstLoad) {
    return (
      <MgmtLayout showBack backTo="/management/projects">
        <div className="space-y-0">
          <SkeletonHero />
          <div className="bg-[#1A1A1A] px-4 py-3">
            <SkeletonStatRow count={4} />
          </div>
          {/* Tab bar skeleton */}
          <div className="bg-white border-b border-gray-100 px-4 flex overflow-x-auto scrollbar-hide flex-shrink-0 items-center gap-1">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 px-4 py-3.5">
                <div className="h-4 bg-gray-100 rounded animate-pulse w-16"></div>
              </div>
            ))}
            <div className="flex-shrink-0 px-4 py-3.5">
              <div className="h-4 bg-gray-100 rounded animate-pulse w-12"></div>
            </div>
          </div>
          <div className="px-4 py-4 max-w-full space-y-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonStageTimeline count={3} />
          </div>
        </div>
      </MgmtLayout>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <MgmtLayout showBack backTo="/management/projects">
      {/* Photo Lightbox */}
      {lightboxIndex !== null && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => { setLightboxIndex(null); setLightboxPhotos([]); }}
        />
      )}

      {/* Hero */}
      <div className="relative h-52 overflow-hidden">
        <img src={coverImage} alt={project.project_name} className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/90 via-[#1A1A1A]/30 to-transparent"></div>

        {/* Edit button */}
        <div className="absolute top-3 right-3">
          <div className="relative">
            <button
              onClick={() => setShowEditMenu((v) => !v)}
              className="w-9 h-9 rounded-xl bg-black/30 backdrop-blur-sm flex items-center justify-center cursor-pointer border border-white/10"
            >
              <i className="ri-more-2-fill text-white text-lg"></i>
            </button>
            {showEditMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowEditMenu(false)}></div>
                <div className="absolute right-0 top-11 bg-white rounded-xl border border-gray-100 overflow-hidden z-20 min-w-[160px]">
                  <button
                    onClick={async () => {
                      setShowEditMenu(false);
                      const { persistProjectSummary } = await import('@/lib/persistProjectSummary');
                      try {
                        await persistProjectSummary(project?.id || '');
                        handleScheduleUpdated();
                      } catch (err) {
                        /* noop */
                      }
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#374151] hover:bg-[#FEF0E7] hover:text-[#E8621A] cursor-pointer transition-colors text-left whitespace-nowrap"
                  >
                    <i className="ri-refresh-line text-base"></i>
                    {t('overview.recalculateProgress')}
                  </button>
                  <button
                    onClick={() => { setShowEditMenu(false); navigate(`/management/projects/${id}/edit`); }}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#374151] hover:bg-[#FEF0E7] hover:text-[#E8621A] cursor-pointer transition-colors text-left whitespace-nowrap"
                  >
                    <i className="ri-edit-line text-base"></i>
                    {t('overview.editProject')}
                  </button>
                  <button
                    onClick={() => { setShowEditMenu(false); navigate(`/management/projects/${id}/edit`); }}
                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#374151] hover:bg-[#FEF0E7] hover:text-[#E8621A] cursor-pointer transition-colors text-left whitespace-nowrap"
                  >
                    <i className="ri-settings-3-line text-base"></i>
                    {t('overview.projectSettings')}
                  </button>
                  <div className="border-t border-gray-100"></div>
                  {isArchived ? (
                    <button
                      onClick={() => { setShowEditMenu(false); handleArchiveProject('restore'); }}
                      disabled={archiveActionLoading}
                      className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#059669] hover:bg-[#F0FDF4] cursor-pointer transition-colors text-left whitespace-nowrap disabled:opacity-50"
                    >
                      <i className="ri-arrow-go-back-line text-base"></i>
                      {archiveActionLoading ? 'Restoring...' : 'Restore Project'}
                    </button>
                  ) : (
                    <button
                      onClick={() => { setShowEditMenu(false); handleArchiveProject('archive'); }}
                      disabled={archiveActionLoading}
                      className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#9CA3AF] hover:bg-[#F5F3FF] hover:text-[#7C3AED] cursor-pointer transition-colors text-left whitespace-nowrap disabled:opacity-50"
                    >
                      <i className="ri-archive-line text-base"></i>
                      {archiveActionLoading ? 'Archiving...' : 'Archive Project'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="absolute bottom-4 left-4 right-14">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider">{project.project_code}</span>
            <span className="text-white/30">·</span>
            <span className="text-[10px] font-bold text-[#E8621A] capitalize">{liveSummary.loaded ? liveSummary.projectStatus : project.status}</span>
            {project.project_type && (
              <>
                <span className="text-white/30">·</span>
                <span className="text-[10px] font-bold text-white/60">{project.project_type}</span>
              </>
            )}
            {liveSummary.loaded && liveSummary.activeStage && (
              <>
                <span className="text-white/30">·</span>
                <span className="text-[10px] font-bold text-white/80">{liveSummary.activeStage}</span>
              </>
            )}
          </div>
          <h1 className="text-white text-xl font-bold leading-tight">{project.project_name}</h1>
          <p className="text-white/60 text-xs mt-0.5">{project.site_address || t('projectDetail.noAddressSet')}</p>
        </div>
      </div>

      {/* Archived banner */}
      {isArchived && (
        <div className="bg-[#F5F3FF] border border-[#7C3AED]/20 px-4 py-3">
          <div className="flex items-center gap-3">
            <i className="ri-archive-line text-[#7C3AED] text-base flex-shrink-0"></i>
            <div className="flex-1 min-w-0">
              <p className="text-[#7C3AED] text-sm font-semibold">
                This project is archived
                {archivedAt ? ` (${new Date(archivedAt).toLocaleDateString()})` : ''}.
                Hidden from active dashboards and lists.
              </p>
            </div>
            <button
              onClick={() => handleArchiveProject('restore')}
              disabled={archiveActionLoading}
              className="px-3 py-1.5 rounded-lg bg-white border border-[#7C3AED]/30 text-[#7C3AED] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#7C3AED] hover:text-white transition-colors disabled:opacity-50"
            >
              {archiveActionLoading ? 'Restoring...' : 'Restore Project'}
            </button>
          </div>
        </div>
      )}

      {/* ── Clean Project Health Summary Card ── */}
      {summaryLoaded && (
        <div className="bg-white border-b border-gray-100">
          {/* Main stats row */}
          <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {/* Overall Progress */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{t('progress.projectProgress')}</span>
              <div className="flex items-end gap-1.5">
                <span className="text-2xl font-bold leading-none" style={{ color: projectProgress === 100 ? '#059669' : '#E8621A' }}>{projectProgress}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${projectProgress}%`, backgroundColor: projectProgress === 100 ? '#059669' : '#E8621A' }}></div>
              </div>
              <span className="text-[10px] text-[#9CA3AF]">Sched {liveSummary.scheduleFinalProgress}% · CL {liveSummary.checklistFinalProgress}%</span>
            </div>

            {/* Management */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{t('progress.management')}</span>
              <div className="flex items-end gap-1.5">
                <span className="text-2xl font-bold leading-none" style={{ color: mgmtPct === 100 ? '#059669' : '#E8621A' }}>{managementCompleted}</span>
                <span className="text-sm text-[#9CA3AF] mb-0.5">/ {managementTotal}</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${mgmtPct}%`, backgroundColor: mgmtPct === 100 ? '#059669' : '#E8621A' }}></div>
              </div>
              <span className="text-[10px] text-[#9CA3AF]">{mgmtPct === 100 ? t('progress.done') : t('progress.pending')}</span>
            </div>

            {/* Subcontractor (only if applicable) */}
            {subcontractorTotal > 0 ? (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{t('progress.subcontractor')}</span>
                <div className="flex items-end gap-1.5">
                  <span className="text-2xl font-bold leading-none" style={{ color: subPct === 100 ? '#0891B2' : '#F59E0B' }}>{subcontractorCompleted}</span>
                  <span className="text-sm text-[#9CA3AF] mb-0.5">/ {subcontractorTotal}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${subPct}%`, backgroundColor: subPct === 100 ? '#0891B2' : '#F59E0B' }}></div>
                </div>
                <span className="text-[10px]" style={{ color: subWaiting ? '#F59E0B' : '#9CA3AF' }}>{subWaiting ? t('progress.waitingForSub') : t('progress.done')}</span>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{t('progress.stage')}</span>
                <div className="flex items-end gap-1.5">
                  <span className="text-base font-bold leading-none text-[#059669] truncate">{activeStage || '—'}</span>
                </div>
                <div className="h-1.5 bg-transparent"></div>
                <span className="text-[10px] text-[#9CA3AF]">{t('progress.currentStage')}</span>
              </div>
            )}

            {/* Pending items — collapsible trigger */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">{t('projectDetail.pending')}</span>
              <div className="flex items-end gap-1.5">
                <span className="text-2xl font-bold leading-none" style={{ color: liveSummary.pendingScheduleItems === 0 ? '#059669' : '#F59E0B' }}>
                  {liveSummary.pendingScheduleItems}
                </span>
              </div>
              <div className="h-1.5 bg-transparent"></div>
              <button
                onClick={() => setActiveTabIndex(TAB_KEYS.indexOf('Schedule'))}
                className="text-[10px] text-[#E8621A] font-semibold cursor-pointer hover:underline text-left whitespace-nowrap"
              >
                {t('projectDetail.reviewSchedule')}
              </button>
            </div>
          </div>

          {/* Collapsible detail row — only show when there are issues */}
          {(liveSummary.scheduleFinalProgress < 100 || liveSummary.checklistFinalProgress < 100 || subWaiting) && (
            <div className="px-4 pb-3">
              <div className="bg-[#F8FAFC] rounded-xl px-3 py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#6B7280]">
                  {liveSummary.scheduleFinalProgress < 100 && (
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] inline-block"></span>
                      {liveSummary.pendingScheduleItems} {t('projectDetail.scheduleItemsPending')}
                    </span>
                  )}
                  {liveSummary.checklistFinalProgress < 100 && (
                    <>
                      {liveSummary.scheduleFinalProgress < 100 && <span className="text-[#D1D5DB]">·</span>}
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E8621A] inline-block"></span>
                        {liveSummary.checklistFinalTotal - liveSummary.checklistFinalCompleted} {t('projectDetail.checklistItemsPendingShort')}
                      </span>
                    </>
                  )}
                  {subWaiting && (
                    <>
                      <span className="text-[#D1D5DB]">·</span>
                      <span className="flex items-center gap-1 text-[#F59E0B] font-semibold">
                        <i className="ri-time-line text-[10px]"></i>
                        {t('projectDetail.waitingSubcontractor')}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {liveSummary.scheduleFinalProgress < 100 && (
                    <button
                      onClick={() => setActiveTabIndex(TAB_KEYS.indexOf('Schedule'))}
                      className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2.5 py-1 rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#FDBA74]/30 transition-colors"
                    >
                      {t('projectDetail.reviewSchedule')}
                    </button>
                  )}
                  {liveSummary.checklistFinalProgress < 100 && (
                    <button
                      onClick={() => setActiveTabIndex(TAB_KEYS.indexOf('Checklist'))}
                      className="text-[10px] font-bold text-[#059669] bg-[#F0FDF4] px-2.5 py-1 rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#BBF7D0]/30 transition-colors"
                    >
                      {t('projectDetail.reviewChecklist')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stage completion prompt */}
      {showStagePrompt && pendingStageName && (
        <div className="bg-[#FEF0E7] border-b border-[#FDBA74] px-4 py-3">
          <div className="flex items-center gap-3">
            <i className="ri-lightbulb-line text-[#E8621A] text-lg flex-shrink-0"></i>
            <p className="text-[#1A1A1A] text-sm font-semibold flex-1">
              {t('projectDetail.scheduleProgressSuggestsMoving')} <strong className="text-[#E8621A]">{pendingStageName}</strong>.
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => setShowStagePrompt(false)} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap">
                {t('projectDetail.dismiss')}
              </button>
              <button
                onClick={() => {
                  if (project) {
                    supabase.from('jphph_projects').update({ status: pendingStageName }).eq('id', project.id).then(() => {
                      setShowStagePrompt(false);
                      setPendingStageName(null);
                    });
                  }
                }}
                className="px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-xs font-bold cursor-pointer whitespace-nowrap"
              >
                {t('projectDetail.updateProjectStage')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab bar — all tabs visible with responsive wrap (no far-right More menu) */}
      <div className="bg-white border-b border-gray-100 px-2 sm:px-4 flex flex-wrap flex-shrink-0 items-end gap-x-0.5 gap-y-0">
        {[
          { key: 'Overview', label: t('tabs.overview'), idx: 0 },
          { key: 'Schedule', label: t('tabs.schedule'), idx: 2 },
          { key: 'Stages', label: t('tabs.stages'), idx: 1 },
          { key: 'Checklist', label: t('tabs.checklist'), idx: 3 },
          { key: 'Daily Log', label: t('tabs.dailyLog'), idx: 4 },
          { key: 'Photos', label: t('tabs.photos'), idx: 7 },
          { key: 'Instructions', label: t('tabs.instructions'), idx: 5 },
          { key: 'Change Orders', label: t('tabs.changeOrders'), idx: 6 },
          { key: 'Payments', label: t('tabs.payments'), idx: 10 },
          { key: 'Files', label: t('tabs.files'), idx: 8 },
          { key: 'Tasks', label: t('tabs.tasks'), idx: 9 },
          { key: 'Notifications', label: t('tabs.notifications'), idx: 11, badge: unreadNotifCount },
          { key: 'Questions', label: t('tabs.questions'), idx: 12 },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTabIndex(tab.idx)}
            className={`flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap relative ${
              activeTabIndex === tab.idx
                ? 'border-[#E8621A] text-[#E8621A]'
                : 'border-transparent text-[#9CA3AF] hover:text-[#374151]'
            }`}
          >
            <span className="inline-flex items-center gap-1">
              {tab.label}
              {'badge' in tab && tab.badge && tab.badge > 0 ? (
                <span className="w-1.5 h-1.5 rounded-full bg-[#E8621A] flex-shrink-0" />
              ) : null}
            </span>
          </button>
        ))}
      </div>

      {/* Tab content — no fixed height or overflow-y-auto here; let the page scroll naturally on mobile */}
      <div className="w-full max-w-full overflow-x-hidden">
      {activeTab === 'Daily Log' && (
        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          <DailyLogTab projectId={project.id} viewerRole="management" />
        </div>
      )}

      {activeTab === 'Instructions' && (
        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          <ManagementInstructionsTab projectId={project.id} />
        </div>
      )}

      {activeTab === 'Change Orders' && (
        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          <ChangeOrdersTab projectId={project.id} />
        </div>
      )}

      {['Overview', 'Stages', 'Schedule', 'Checklist', 'Photos', 'Files', 'Tasks', 'Payments', 'Notifications', 'Questions'].includes(activeTab) && (
        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          {/* Overview Tab */}
          {activeTab === 'Overview' && (
            <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: t('tabs.dailyLog'), icon: 'ri-book-open-line', color: '#059669', tab: 'Daily Log' },
                { label: t('tabs.schedule'), icon: 'ri-calendar-schedule-line', color: '#E8621A', tab: 'Schedule' },
                { label: t('tabs.tasks'), icon: 'ri-task-line', color: '#7C3AED', tab: 'Tasks' },
                { label: t('tabs.notifications'), icon: 'ri-notification-3-line', color: '#374151', tab: 'Notifications' },
              ].map((q) => (
                <button key={q.tab} onClick={() => setActiveTabIndex(TAB_KEYS.indexOf(q.tab))}
                  className="bg-white rounded-2xl border border-gray-100 p-3 flex items-center gap-2.5 cursor-pointer hover:border-gray-200 transition-colors">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${q.color}15` }}>
                    <i className={`${q.icon} text-base`} style={{ color: q.color }}></i>
                  </div>
                  <span className="text-[#374151] text-sm font-semibold">{q.label}</span>
                </button>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <h3 className="font-bold text-[#1A1A1A] text-sm mb-3">{t('overview.projectDetails')}</h3>
              <div className="space-y-2.5">
                {[
                  { label: t('overview.client'), value: project.client_name || '—' },
                  { label: t('overview.type'), value: project.project_type || '—' },
                  { label: t('overview.startDate'), value: project.start_date ? formatDate(project.start_date) : '—' },
                  { label: t('overview.estCompletion'), value: project.target_completion_date ? formatDate(project.target_completion_date) : '—' },
                  { label: t('overview.status'), value: liveSummary.loaded ? liveSummary.projectStatus : project.status },
                  { label: t('overview.currentStage'), value: liveSummary.loaded ? liveSummary.activeStage : '—' },
                  { label: t('overview.projectProgress'), value: `${liveSummary.projectProgress}%` },
                  { label: t('overview.scheduleFinal'), value: `${liveSummary.scheduleFinalProgress}%` },
                  { label: t('overview.checklistFinal'), value: `${liveSummary.checklistFinalProgress}%` },
                  { label: t('overview.pendingScheduleItems'), value: `${liveSummary.pendingScheduleItems}` },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between">
                    <span className="text-[#9CA3AF] text-sm">{row.label}</span>
                    <span className={`text-[#374151] text-sm font-medium capitalize ${row.label === 'Status' && liveSummary.projectStatus === 'complete' ? 'text-[#059669]' : ''}`}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {project.summary && (
              <div className="bg-white rounded-2xl border border-gray-100 p-4">
                <h3 className="font-bold text-[#1A1A1A] text-sm mb-2">{t('projectDetail.description')}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{project.summary}</p>
              </div>
            )}

            {user && <ProjectIntentSection projectId={project.id} userId={user.id} projectType={project.project_type} mode="edit" labelStyle="internal" />}
            {user && <ProjectStoryTimeline projectId={project.id} userId={user.id} mode="management" labelStyle="internal" />}
          </div>
        )}

        {activeTab === 'Stages' && (
          <StagesTab projectId={project.id} />
        )}

        {activeTab === 'Photos' && <PhotosTab projectId={project.id} canUpload={true} />}
        {activeTab === 'Files' && <FilesTab projectId={project.id} canUpload={true} />}

        {activeTab === 'Questions' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-[#1A1A1A] text-sm">{t('questions.clientQuestions')}</h3>
              <span className="text-[#9CA3AF] text-xs">{questions.filter((q) => q.status !== 'answered' && !questionReplies.find((r) => r.question_id === q.id)).length} {t('questions.unanswered')}</span>
            </div>

            {questionAttachmentOpenError && (
              <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-3 py-2.5 text-[#EF4444] text-xs">
                {questionAttachmentOpenError}
              </div>
            )}

            {questions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
                  <i className="ri-question-answer-line text-4xl"></i>
                </div>
                <p className="text-[#374151] font-semibold text-sm">{t('questions.noQuestions')}</p>
                <p className="text-[#9CA3AF] text-xs mt-1">{t('questions.clientQuestionsAppear')}</p>
              </div>
            ) : (
              questions.map((q) => {
                const qReplies = questionReplies.filter((r) => r.question_id === q.id);
                const isAnswered = q.status === 'answered' || qReplies.length > 0;
                const qAttachments = questionAttachments.filter((a) => a.question_id === q.id);
                return (
                  <div key={q.id} className={`bg-white rounded-2xl border overflow-hidden ${!isAnswered ? 'border-[#E8621A]/30' : 'border-gray-100'}`}>
                    {!isAnswered && <div className="h-1 bg-[#E8621A]"></div>}
                    <div className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-[#374151] flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-white text-sm"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[#374151] text-xs font-bold">{q.asker_name || 'Client'}</span>
                            <span className="text-[#9CA3AF] text-xs">{timeAgo(q.created_at)}</span>
                          </div>
                          <p className="text-[#1A1A1A] text-sm font-bold mt-1 leading-snug">{q.question_title}</p>
                          <p className="text-[#374151] text-sm mt-1 leading-snug">{q.body}</p>
                        </div>
                      </div>

                      {qAttachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {qAttachments.map((att) => {
                            const displayUrl = questionAttachmentUrls[att.id] || resolveFileDisplayUrl(att);
                            if (att.file_type && ['jpg','jpeg','png','webp','gif'].includes(att.file_type.toLowerCase())) {
                              return (
                                <button
                                  key={att.id}
                                  onClick={() => openLightbox([{ url: displayUrl, caption: att.file_name }], 0)}
                                  className="relative w-24 h-20 rounded-xl overflow-hidden border border-gray-100 cursor-pointer group"
                                >
                                  {displayUrl ? (
                                    <img src={displayUrl} alt={att.file_name} loading="lazy" className="w-full h-full object-cover object-center" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[10px] text-[#9CA3AF] p-2 text-center">
                                      Preview unavailable
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                    <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100 text-lg transition-opacity"></i>
                                  </div>
                                </button>
                              );
                            }
                            return (
                              <button
                                key={att.id}
                                type="button"
                                onClick={() => {
                                  void (async () => {
                                    const result = await openProjectFileInNewTab(att);
                                    if (!result.ok) {
                                      setQuestionAttachmentOpenError(result.error || FILE_OPEN_ERROR_MESSAGE);
                                    } else {
                                      setQuestionAttachmentOpenError('');
                                    }
                                  })();
                                }}
                                className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-100 hover:border-gray-200 transition-colors cursor-pointer"
                              >
                                <div className="w-8 h-8 rounded-lg bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                                  <i className="ri-file-pdf-line text-[#E8621A] text-sm"></i>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[#374151] text-xs font-medium truncate max-w-[140px]">{att.file_name}</p>
                                  <p className="text-[#9CA3AF] text-[10px]">PDF</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {isAnswered ? (
                        <div className="space-y-2">
                          {qReplies.map((reply) => (
                            <div key={reply.id} className="ml-11 bg-[#FEF0E7]/50 rounded-xl p-3">
                              <div className="flex items-center gap-1.5 mb-1">
                                <i className="ri-building-2-line text-[#E8621A] text-[10px]"></i>
                                <span className="text-[#E8621A] text-[10px] font-bold">{t('questions.managementReply')} · {timeAgo(reply.created_at)}</span>
                              </div>
                              <p className="text-[#374151] text-sm leading-snug">{reply.body}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="ml-11 space-y-2">
                          <textarea
                            value={answerText?.[q.id] || ''}
                            onChange={(e) => setAnswerText((p) => ({ ...p, [q.id]: e.target.value }))}
                            placeholder={t('questions.typeReply')}
                            rows={2}
                            maxLength={500}
                            className="w-full bg-white border border-[#E0DDD8] rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] placeholder-[#CCCCCC] resize-none"
                          />
                          <button
                            onClick={() => submitAnswer(q.id)}
                            disabled={!answerText?.[q.id]?.trim()}
                            className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50"
                          >
                            <i className="ri-send-plane-line"></i>{t('questions.sendReply')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    )}

      {activeTab === 'Schedule' && (
        <div className="w-full max-w-full overflow-x-hidden">
          <ScheduleTab
            projectId={project.id}
            canEdit={true}
            projectType={project.project_type}
            projectName={project.project_name}
            onSendInstruction={openInstructionForSchedule}
            onScheduleUpdated={handleScheduleUpdated}
          />
        </div>
      )}

      {activeTab === 'Checklist' && (
        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          <ChecklistTab
            projectId={project.id}
            viewerRole="management"
            onRequestAction={(item) => {
              setInstructionLinkedScheduleItemId(undefined);
              setInstructionLinkedChecklistItemId(item.id);
              setInstructionLinkedPhotoId(undefined);
              setInstructionDefaultTitle(`Action required: ${item.title}`);
              setShowInstructionModal(true);
            }}
          />
        </div>
      )}

      {activeTab === 'Tasks' && (
        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          <TasksTab projectId={project.id} canCreate={true} viewerRole="management" />
        </div>
      )}

      {activeTab === 'Payments' && (
        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          <PaymentsTab projectId={project.id} />
        </div>
      )}

      {activeTab === 'Notifications' && (
        <div className="px-4 py-4 max-w-full overflow-x-hidden">
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-[#1A1A1A] text-sm">{t('notifications.projectNotifications')}</h3>
                {unreadNotifCount > 0 && <span className="bg-[#E8621A] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadNotifCount}</span>}
              </div>
              {unreadNotifCount > 0 && (
                <button onClick={markAllNotifsRead} className="text-[#E8621A] text-xs font-medium cursor-pointer whitespace-nowrap">{t('notifications.markAllRead')}</button>
              )}
            </div>

            {loadingNotifs ? (
              <div className="space-y-2">
                <SkeletonListRow />
                <SkeletonListRow />
                <SkeletonListRow />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
                  <i className="ri-notification-off-line text-4xl"></i>
                </div>
                <p className="text-[#374151] font-semibold text-sm">{t('notifications.noNotifications')}</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button key={n.id}
                  onClick={() => { setSelectedNotif(n); if (!n.is_read) markNotifRead(n.id); }}
                  className={`w-full text-left rounded-2xl border p-4 flex items-start gap-3 cursor-pointer transition-colors ${!n.is_read ? 'bg-white border-gray-100' : 'bg-[#F9FAFB] border-transparent'}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${n.color}15` }}>
                    <i className={`${n.icon} text-lg`} style={{ color: n.color }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-[#1A1A1A]' : 'font-medium text-[#374151]'}`}>{n.title}</p>
                      {!n.is_read && <div className="w-2 h-2 rounded-full bg-[#E8621A] flex-shrink-0 mt-1"></div>}
                    </div>
                    <p className="text-[#9CA3AF] text-xs mt-0.5 leading-snug line-clamp-1">{n.body}</p>
                    <span className="text-[#9CA3AF] text-xs">{timeAgo(n.created_at)}</span>
                  </div>
                  <i className="ri-arrow-right-s-line text-[#D1D5DB] text-lg flex-shrink-0 mt-0.5"></i>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Post Update Modal */}
      {showPostModal && (
        <PostUpdateModal projectId={project.id} projectDbId={project.id} onClose={() => setShowPostModal(false)} onSuccess={handlePostSuccess} />
      )}

      {/* Notification Detail Sheet */}
      {selectedNotif && (
        <NotificationDetailSheet notification={selectedNotif} onClose={() => setSelectedNotif(null)} />
      )}

      {/* Instruction Create Modal */}
      {showInstructionModal && (
        <NewInstructionModal
          show={showInstructionModal}
          onClose={() => setShowInstructionModal(false)}
          projectId={project.id}
          linkedScheduleItemId={instructionLinkedScheduleItemId}
          linkedChecklistItemId={instructionLinkedChecklistItemId}
          linkedPhotoId={instructionLinkedPhotoId}
          defaultTitle={instructionDefaultTitle}
          source="project-detail"
          onSuccess={() => {
            setShowInstructionModal(false);
          }}
        />
      )}
    </div>
    </MgmtLayout>
  );
}